import { PublicClientApplication, EventType, InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalConfig, passwordResetRequest, loginRequest, graphConfig } from "./msalConfig";

const msalInstance = new PublicClientApplication(msalConfig);

// Initialize once and share the promise so callers can await readiness before calling APIs.
const msalInitPromise = msalInstance.initialize?.() ?? Promise.resolve();

export const ensureMsalInitialized = async () => {
  try {
    await msalInitPromise;
  } catch (err) {
    // Surface the error to callers so they can handle or surface to users.
    throw err;
  }
};

const authorityHost = (() => {
  try {
    return new URL(msalConfig.auth.authority).host.toLowerCase();
  } catch (e) {
    return "";
  }
})();

const accountMatchesAuthority = (account) =>
  !authorityHost || account?.environment?.toLowerCase().includes(authorityHost);

export const getPreferredAccount = () => {
  const active = msalInstance.getActiveAccount();
  if (active && accountMatchesAuthority(active)) return active;

  const matching = msalInstance
    .getAllAccounts()
    .filter((acct) => accountMatchesAuthority(acct));

  if (matching.length > 0) {
    msalInstance.setActiveAccount(matching[0]);
    return matching[0];
  }

  return null;
};

const setActiveAccountIfMissing = (instance) => {
  const preferred = getPreferredAccount();
  if (!preferred) return;
  if (preferred && instance.getActiveAccount()?.homeAccountId !== preferred.homeAccountId) {
    instance.setActiveAccount(preferred);
  }
};

const buildDesiredDisplayName = (account) => {
  const claims = account?.idTokenClaims || {};
  const surname = claims.family_name || claims.surname || "";
  const givenname = claims?.givenname || claims.preferred_username || (claims.emails?.[0] || "");
  const candidate = [givenname, surname].filter(Boolean).join(" ").trim();
  return candidate || null;
};

const syncDisplayNameIfNeeded = async (account) => {
  const desired = buildDesiredDisplayName(account);
  if (!desired) return;

  const current = (account?.idTokenClaims?.displayName || account?.name || "").trim();
  console.log({ current, desired },(current && current.toLowerCase() === desired.toLowerCase()));
  if (current && current.toLowerCase() === desired.toLowerCase()) return;

  try {
    const tokenResponse = await msalInstance.acquireTokenSilent({
      account,
      scopes: graphConfig.scopes,
    });

    const response = await fetch(`${graphConfig.baseUrl}/me`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${tokenResponse.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ displayName: desired }),
    });

    if (!response.ok) {
      console.warn("Unable to update displayName", await response.text());
      return;
    }

    try {
      await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
        forceRefresh: true,
      });
    } catch (refreshErr) {
      console.warn("Token refresh after displayName update failed", refreshErr);
    }
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      console.warn("Graph consent required to set displayName; pending admin consent.");
      return;
    }
    console.warn("Failed to sync displayName", err);
  }
};

msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_FAILURE && event.error?.errorMessage?.includes("AADB2C90118")) {
    if (passwordResetRequest?.authority) {
      msalInstance.loginRedirect(passwordResetRequest);
    }
    return;
  }

  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
    msalInstance.setActiveAccount(event.payload.account);
    syncDisplayNameIfNeeded(event.payload.account);
  }

  if (event.eventType === EventType.HANDLE_REDIRECT_END) {
    setActiveAccountIfMissing(msalInstance);
  }
});

ensureMsalInitialized()
  .then(() => setActiveAccountIfMissing(msalInstance))
  .catch((err) => {
    // Initialization failures should be visible for troubleshooting.
    console.warn("MSAL init failed", err);
  })
  .then(() => {
    const active = msalInstance.getActiveAccount();
    if (active) {
      syncDisplayNameIfNeeded(active);
    }
  });

export default msalInstance;
